import React, { useState, useEffect, useRef, useCallback } from "react";
import SettingsTitle from "./settings-ui-components/SettingsTitle";
import SettingsCard from "./settings-ui-components/SettingsCard";
import { SettingSwitch } from "../SettingSwitch";
import { Tooltip } from "@vibe/core";
import ButtonComponent from "../../../Button";
import { Info } from "@vibe/icons";
import useBoardAndItems from "../../../../hooks/context/useBoardAndItems";
import useTokenHook from "../../../../hooks/context/useTokenHook";
import logger from "../../../../lib/logger";
import { addItemLocationsColumn } from "../../../../utils/requests/addItemLocationsColumn";
import { seedBoardsColumn } from "../../../../utils/requests/seedBoardsColumn";
import WebhooksSettings from "./settings-ui-components/WebhooksSettings";
import useAuthorizedBoardUsers from "../../../../hooks/api/useAuthorizedUsers";
import { useAccountSettings, useBoardSettings } from "../../../../hooks/api";
import useUser from "../../../../hooks/context/userHook";
import useItemLocationsSeedStream, {
  ItemLocationsSeedCompletedEvent,
} from "../../../../hooks/services/useItemLocationsSeedStream";
import useBoardsColumnSeedStream, {
  BoardsColumnSeedCompletedEvent,
} from "../../../../hooks/services/useBoardsColumnSeedStream";
import UpdatesSettings from "./settings-ui-components/UpdatesSettings";
import SubitemsSettings from "./settings-ui-components/SubitemsSettings";
import { useConnectedBoards } from "../../../../hooks/api";
import { MondayApiQueryInstance } from "../../../../services/monday/monday-service-ts";

const ITEM_LOCATIONS_TOAST_MS = 5000;
const ITEM_LOCATIONS_QUEUED_TOAST_MS = 8000;
/** The backend's 409 code when the account has Item Locations turned off. */
export const ITEM_LOCATIONS_DISABLED_CODE = "ITEM_LOCATIONS_DISABLED";
export const ITEM_LOCATIONS_DISABLED_MESSAGE =
  'Turn on "Item Locations Column" in Account Settings → Preferences, then try again.';

/** The info shown next to the "Shared Boards 🔒 column" setting; the column itself is user-added. */
export const BOARDS_COLUMN_INFO =
  'Fills the "Shared Boards 🔒" column with the names of every board each item is connected to, on this board and its connected boards. ' +
  'It only works on boards that already have the "Shared Boards 🔒" column — add it from the column center (Same Item Multiple Boards) first.';

const BoardSettings = () => {
  const { itemIds, currentBoard } = useBoardAndItems();
  const { token } = useTokenHook();
  const { userId, accountId } = useUser();
  const { accountSettingsData } = useAccountSettings();
  const itemLocationsEnabled = accountSettingsData?.item_location_setting !== false;
  // requestIds of "Add column" clicks the backend queued (202) and that have not completed yet.
  const pendingSeedRequestsRef = useRef<Set<string>>(new Set());
  // Same for "Revalidate" clicks on the Shared Boards column.
  const pendingBoardsSeedRequestsRef = useRef<Set<string>>(new Set());
  const [revalidateBoardsLoading, setRevalidateBoardsLoading] = useState(false);
  const [parentBoard, setParentBoard] = useState<number | null>(null);
  const [addColumnLoading, setAddColumnLoading] = useState(false);
  const { authorizedUsers, error, isLoading } = useAuthorizedBoardUsers(parentBoard!);
  const { boardSettingsData, updateBoardSetting } = useBoardSettings(parentBoard!);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [switchValues, setSwitchValues] = useState<any>(null);

  const { connectedBoardsData } = useConnectedBoards(token!, currentBoard!);

  useEffect(() => {
    const checkParentBoardId = async (currentBoard: number, itemIds: number[]) => {
      try {
        const response: any = await MondayApiQueryInstance.getItemNames(itemIds);

        if (response?.data?.items[0]?.parent_item) {
          setParentBoard(response?.data?.items[0]?.parent_item?.board?.id);
        } else {
          setParentBoard(currentBoard);
        }
      } catch (error: any) {
        logger.error("Error checking parent board id:", error);
        throw error;
      }
    };
    if (currentBoard && itemIds && itemIds.length > 0) {
      checkParentBoardId(currentBoard, itemIds);
    }
  }, [currentBoard, itemIds]);

  useEffect(() => {
    if (boardSettingsData) {
      setSwitchValues(boardSettingsData);
    }
  }, [boardSettingsData]);

  const handleUpdatePreferenceChange = async (
    preference: string,
    excludedBoardIds: string[] = [],
  ) => {
    try {
      if (excludedBoardIds.length > 0 || preference === "sync_to_all_except") {
        await updateBoardSetting("updates", preference, preference, excludedBoardIds);
      } else {
        await updateBoardSetting("updates", preference, preference);
      }
    } catch (error: any) {
      logger.error("Error updating update preference:", error);
    }
  };

  const handleSubitemPreferenceChange = async (
    preference: string,
    excludedBoardIds: string[] = [],
  ) => {
    try {
      if (excludedBoardIds.length > 0 || preference === "sync_to_all_except") {
        await updateBoardSetting("subitems", preference, preference, excludedBoardIds);
      } else {
        await updateBoardSetting("subitems", preference, preference);
      }
    } catch (error: any) {
      logger.error("Error updating subitem preference:", error);
    }
  };

  // Completion of a queued click, over the notification socket: one message per requestId once
  // every chunk job finished. Unlike the item view there is no optimistic state to reconcile
  // and a seed of thousands of items can legitimately take a long time, so there is no
  // fallback timer - a completion that never arrives just means no toast.
  const handleSeedCompleted = useCallback((event: ItemLocationsSeedCompletedEvent) => {
    if (!pendingSeedRequestsRef.current.delete(event.requestId)) return;

    if (event.reason === "no_sync_group") {
      MondayApiQueryInstance.mondayToastNotification(
        "No connected items found on this board, nothing to fill.",
        "info",
        ITEM_LOCATIONS_TOAST_MS,
      );
      return;
    }

    MondayApiQueryInstance.mondayToastNotification(
      event.message,
      event.summary?.dead > 0 ? "error" : "success",
      ITEM_LOCATIONS_TOAST_MS,
    );
  }, []);

  useItemLocationsSeedStream({
    userId: userId ?? undefined,
    accountId: accountId ?? undefined,
    onCompleted: handleSeedCompleted,
  });

  const handleBoardsSeedCompleted = useCallback((event: BoardsColumnSeedCompletedEvent) => {
    if (!pendingBoardsSeedRequestsRef.current.delete(event.requestId)) return;

    if (event.reason === "no_sync_group") {
      MondayApiQueryInstance.mondayToastNotification(
        "No connected items found on this board, nothing to fill.",
        "info",
        ITEM_LOCATIONS_TOAST_MS,
      );
      return;
    }

    // "no_column": none of the boards has the column yet — the message says so.
    MondayApiQueryInstance.mondayToastNotification(
      event.message,
      event.reason === "no_column" ? "info" : event.summary?.dead > 0 ? "error" : "success",
      ITEM_LOCATIONS_QUEUED_TOAST_MS,
    );
  }, []);

  useBoardsColumnSeedStream({
    userId: userId ?? undefined,
    accountId: accountId ?? undefined,
    onCompleted: handleBoardsSeedCompleted,
  });

  const handleRevalidateBoardsColumn = async () => {
    try {
      setRevalidateBoardsLoading(true);
      const response = await seedBoardsColumn(token!, currentBoard!);

      if (response?.status === 202 && response?.data?.requestId) {
        pendingBoardsSeedRequestsRef.current.add(response.data.requestId);
        await MondayApiQueryInstance.mondayToastNotification(
          "Shared Boards 🔒 column queued: filling it on this board and its connected boards in the background. You will be notified when it is done.",
          "info",
          ITEM_LOCATIONS_QUEUED_TOAST_MS,
        );
        return;
      }

      await MondayApiQueryInstance.mondayToastNotification(
        "Shared Boards 🔒 column revalidated",
        "success",
        ITEM_LOCATIONS_TOAST_MS,
      );
    } catch (error: any) {
      logger.error("Error revalidating Shared Boards column:", error);

      const message = error?.response?.data?.error_message ?? error.message;

      await MondayApiQueryInstance.mondayToastNotification(
        message,
        "error",
        ITEM_LOCATIONS_TOAST_MS,
      );
    } finally {
      setRevalidateBoardsLoading(false);
    }
  };

  const handleAddColumn = async () => {
    if (!itemLocationsEnabled) {
      await MondayApiQueryInstance.mondayToastNotification(
        ITEM_LOCATIONS_DISABLED_MESSAGE,
        "error",
        ITEM_LOCATIONS_TOAST_MS,
      );
      return;
    }

    try {
      setAddColumnLoading(true);
      const response = await addItemLocationsColumn(token!, currentBoard!);

      // 202: the backend queued the fill as orchestration jobs. The links land in the
      // background; the completion toast comes over the socket, keyed on requestId.
      if (response?.status === 202 && response?.data?.requestId) {
        pendingSeedRequestsRef.current.add(response.data.requestId);
        await MondayApiQueryInstance.mondayToastNotification(
          "Item Locations queued: filling the column on this board and its connected boards in the background. You will be notified when it is done.",
          "info",
          ITEM_LOCATIONS_QUEUED_TOAST_MS,
        );
        return;
      }

      await MondayApiQueryInstance.mondayToastNotification(
        "Item Locations column added successfully",
        "success",
        ITEM_LOCATIONS_TOAST_MS,
      );
    } catch (error: any) {
      logger.error("Error adding column:", error);

      // The backend refuses with 409 when the account has the feature off, so a stale settings
      // read here still ends in the right message.
      const message =
        error?.response?.data?.code === ITEM_LOCATIONS_DISABLED_CODE
          ? ITEM_LOCATIONS_DISABLED_MESSAGE
          : (error?.response?.data?.error_message ?? error.message);

      await MondayApiQueryInstance.mondayToastNotification(
        message,
        "error",
        ITEM_LOCATIONS_TOAST_MS,
      );
    } finally {
      setAddColumnLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <SettingsTitle
        title="Board Settings"
        subtitle="Manage the board settings of the current board you are in"
      />
      {/* Notifications */}
      <SettingsCard title="Notifications">
        <div className="space-y-2">
          <SettingSwitch
            label="Notify users when their update didn't sync to other boards"
            initialValue={boardSettingsData?.notifications?.notify_on_update_not_synced}
            settingKey="notify_on_update_not_synced"
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateBoardSetting("notifications", "notify_on_update_not_synced", value)
            }
          />
          <SettingSwitch
            label="Notify users when the item already exists on the board"
            initialValue={boardSettingsData?.notifications?.notify_on_item_already_exists}
            settingKey="notify_on_item_already_exists"
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateBoardSetting("notifications", "notify_on_item_already_exists", value)
            }
          />
          <SettingSwitch
            label="Notify users when a shared column failed to sync"
            initialValue={boardSettingsData?.notifications?.notify_on_shared_column_failed_sync}
            settingKey="notify_on_shared_column_failed_sync"
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateBoardSetting("notifications", "notify_on_shared_column_failed_sync", value)
            }
          />
          <SettingSwitch
            label="Notify users when a target board has duplicate columns"
            tooltip="Sent when a board this board syncs to has two or more columns with the same name and type. Only the first of them stays in sync."
            // Boards saved before this setting existed have no value for it; it is on by default.
            initialValue={
              boardSettingsData
                ? (boardSettingsData.notifications?.notify_on_duplicate_target_columns ?? true)
                : undefined
            }
            settingKey="notify_on_duplicate_target_columns"
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateBoardSetting("notifications", "notify_on_duplicate_target_columns", value)
            }
          />
        </div>
      </SettingsCard>

      {/* Items */}
      <SettingsCard title="Items">
        <div className="space-y-2">
          <SettingSwitch
            label="Support moving items to this board"
            initialValue={boardSettingsData?.move_item_setting}
            settingKey="move_item_setting"
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateBoardSetting("move_item", "move_item_setting", value)}
          />

          <div className="flex justify-between items-start">
            <div className="space-x-2">
              <div className="flex flex-row gap-2">
                <p className="text-sm">Item Locations</p>
                <div className="flex items-center">
                  <Tooltip
                    zIndex={1210}
                    content={
                      itemLocationsEnabled
                        ? "Adds a column to the current board and its connected boards that displays the locations of all shared items"
                        : ITEM_LOCATIONS_DISABLED_MESSAGE
                    }
                  >
                    <Info />
                  </Tooltip>
                </div>
              </div>
            </div>

            <ButtonComponent
              text="Add column"
              size="small"
              onClick={handleAddColumn}
              loading={addColumnLoading}
              disabled={!itemLocationsEnabled}
            />
          </div>

          <div className="flex justify-between items-start">
            <div className="space-x-2">
              <div className="flex flex-row gap-2">
                <p className="text-sm">Shared Boards 🔒 column</p>
                <div className="flex items-center">
                  <Tooltip zIndex={1210} content={BOARDS_COLUMN_INFO}>
                    <Info />
                  </Tooltip>
                </div>
              </div>
            </div>

            <ButtonComponent
              text="Revalidate"
              size="small"
              onClick={handleRevalidateBoardsColumn}
              loading={revalidateBoardsLoading}
            />
          </div>
        </div>
      </SettingsCard>

      {/* Updates */}
      <SettingsCard title="Updates">
        <UpdatesSettings
          boardSettingsData={boardSettingsData}
          onUpdatePreferenceChange={handleUpdatePreferenceChange}
          options={connectedBoardsData}
        />
      </SettingsCard>

      {/* Subitems */}
      <SettingsCard title="Subitems">
        <SubitemsSettings
          boardSettingsData={boardSettingsData}
          onSubitemPreferenceChange={handleSubitemPreferenceChange}
          options={connectedBoardsData}
        />
      </SettingsCard>

      {/* Webhooks */}
      <SettingsCard
        title="Webhooks"
        subtitle="Manage your webhooks on this board. Please ensure that you or the person you are transferring ownership to is an owner of the board. "
      >
        <div className="space-y-2">
          <WebhooksSettings
            boardId={parentBoard}
            authorizedUsers={authorizedUsers}
            authorizedUsersLoading={isLoading}
            authorizedUsersError={error}
          />
        </div>
      </SettingsCard>
    </div>
  );
};

export default BoardSettings;
